import React, { useState, useEffect } from 'react';
import { APP_CONFIG, STORAGE_KEYS } from '../../constants';
import { detectMobileDevice, openAppOrStore, openAppStore } from '../../utils/mobileAppDetection';
import '../../styles/components/mobile-app-prompt.scss';

interface MobileAppPromptProps {
  onDismiss?: () => void;
}

const MobileAppPrompt: React.FC<MobileAppPromptProps> = ({ onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState(detectMobileDevice());
  const [appInstalled, setAppInstalled] = useState<boolean | null>(null);

  useEffect(() => {
    // Only show on mobile devices
    if (!deviceInfo.isMobile) {
      return;
    }

    // Check if user has dismissed the prompt and if cooldown period has passed
    const dismissedTimestamp = localStorage.getItem(STORAGE_KEYS.APP_PROMPT_DISMISSED);
    
    if (dismissedTimestamp) {
      // If cooldown is null, never show again
      if (APP_CONFIG.PROMPT_COOLDOWN_HOURS === null) {
        return;
      }
      
      // If cooldown is 0, always show (ignore dismissal)
      if (APP_CONFIG.PROMPT_COOLDOWN_HOURS === 0) {
        // Show it anyway
      } else {
        // Check if cooldown period has passed
        const dismissedTime = parseInt(dismissedTimestamp, 10);
        const now = Date.now();
        const cooldownMs = APP_CONFIG.PROMPT_COOLDOWN_HOURS * 60 * 60 * 1000;
        
        if (now - dismissedTime < cooldownMs) {
          return; // Still in cooldown period
        }
      }
    }

    // Show the prompt with a small delay for better UX
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 1000); // 1 second delay after page load

    return () => clearTimeout(timer);
  }, [deviceInfo.isMobile]);

  const handleDismiss = () => {
    // Store the current timestamp instead of just 'true'
    localStorage.setItem(STORAGE_KEYS.APP_PROMPT_DISMISSED, Date.now().toString());
    setIsVisible(false);
    if (onDismiss) {
      onDismiss();
    }
  };

  const handleOpenApp = async () => {
    await openAppOrStore(
      APP_CONFIG.DEEP_LINK_SCHEME,
      APP_CONFIG.IOS_APP_STORE_URL,
      APP_CONFIG.ANDROID_PLAY_STORE_URL,
      deviceInfo
    );
    // Dismiss after attempting to open
    handleDismiss();
  };

  const handleDownloadApp = () => {
    openAppStore(
      APP_CONFIG.IOS_APP_STORE_URL,
      APP_CONFIG.ANDROID_PLAY_STORE_URL,
      deviceInfo
    );
    // Don't dismiss immediately - let user download
  };

  if (!isVisible) return null;

  return (
    <div className="mobile-app-prompt-overlay">
      <div className="mobile-app-prompt">
        <button
          className="mobile-app-prompt-close"
          onClick={handleDismiss}
          aria-label="Close"
        >
          <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="mobile-app-prompt-content">
          <div className="mobile-app-prompt-icon">
            <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
          </div>

          <h2 className="mobile-app-prompt-title">
            Get the {APP_CONFIG.APP_NAME} App
          </h2>
          <p className="mobile-app-prompt-subtitle">
            Enjoy a better experience with our mobile app
          </p>

          <div className="mobile-app-prompt-actions">
            <button
              className="mobile-app-prompt-btn mobile-app-prompt-btn-primary"
              onClick={handleOpenApp}
            >
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
              </svg>
              Open App
            </button>
            <button
              className="mobile-app-prompt-btn mobile-app-prompt-btn-secondary"
              onClick={handleDownloadApp}
            >
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
              </svg>
              Download App
            </button>
          </div>

          <button
            className="mobile-app-prompt-dismiss"
            onClick={handleDismiss}
          >
            Continue on website
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileAppPrompt;


